/**
 *  @class
 *  @function CountdownTimer
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();

      this.io = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.init();
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: `0px 0px 500px 0px` });

      this.io.observe(this);
    }
    init() {
      const timezone = (this.dataset.timezone || '').trim(),
        date = this.dataset.date.split('-'),
        day = parseInt(date[0]),
        month = parseInt(date[1]),
        year = parseInt(date[2]);

      let time = this.dataset.time,
        tarhour = 0,
        tarmin = 0;

      if (time) {
        [tarhour, tarmin] = time.split(':').map(Number);
      }

      this.countDownDate = CountdownTimer.resolveTargetDate({
        year,
        month,
        day,
        hour: tarhour,
        minute: tarmin,
        timezone
      });
    }
    convertDateForIos(date) {
      var arr = date.split(/[- :]/);
      date = new Date(arr[0], arr[1] - 1, arr[2], arr[3], arr[4], arr[5]);
      return date;
    }
    connectedCallback() {

      const daysEl = this.querySelector('.days .countdown-timer--column--number'),
        hoursEl = this.querySelector('.hours .countdown-timer--column--number'),
        minutesEl = this.querySelector('.minutes .countdown-timer--column--number'),
        secondsEl = this.querySelector('.seconds .countdown-timer--column--number');

      // Wrap each number in a clip container with an initial current span.
      // The clip container gets overflow:hidden; the number element keeps its
      // :before colon pseudo-element outside the clipping context.
      [daysEl, hoursEl, minutesEl, secondsEl].forEach(el => {
        const clip = document.createElement('span');
        clip.className = 'countdown-clip';
        const current = document.createElement('span');
        current.className = 'countdown-num--current';
        current.textContent = el.textContent;
        el.textContent = '';
        clip.appendChild(current);
        el.appendChild(clip);
      });

      const updateTime = () => {
        // Wait until init() has set countDownDate via IntersectionObserver
        if (!this.countDownDate) {
          requestAnimationFrame(updateTime);
          return;
        }

        // Get todays date and time
        const now = Date.now();

        // Find the distance between now an the count down date
        const distance = this.countDownDate - now;

        if (distance < 0) {
          CountdownTimer.setNumber(daysEl, '00');
          CountdownTimer.setNumber(hoursEl, '00');
          CountdownTimer.setNumber(minutesEl, '00');
          CountdownTimer.setNumber(secondsEl, '00');
          return;
        }

        // Time calculations for days, hours, minutes and seconds
        const days = Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds = Math.floor((distance % (1000 * 60)) / 1000);

        requestAnimationFrame(updateTime);

        CountdownTimer.setNumber(daysEl, CountdownTimer.addZero(days));
        CountdownTimer.setNumber(hoursEl, CountdownTimer.addZero(hours));
        CountdownTimer.setNumber(minutesEl, CountdownTimer.addZero(minutes));
        CountdownTimer.setNumber(secondsEl, CountdownTimer.addZero(seconds));

      };
      requestAnimationFrame(updateTime);
    }
    static setNumber(el, newVal) {
      newVal = String(newVal);
      const clip = el.querySelector('.countdown-clip');
      const entering = clip.querySelector('.countdown-num--entering');
      const current = clip.querySelector('.countdown-num--current');
      // Guard: already showing or already animating to this value
      if ((entering || current) && (entering || current).textContent === newVal) return;

      // Cancel any in-flight animations
      const leaving = clip.querySelector('.countdown-num--leaving');
      if (leaving) leaving.remove();
      if (entering) entering.remove();

      // Animate current out
      if (current) {
        current.className = 'countdown-num--leaving';
        current.addEventListener('animationend', () => current.remove(), { once: true });
        // Fallback for contexts where no CSS animation is defined.
        setTimeout(() => {
          if (current && current.parentNode) {
            current.remove();
          }
        }, 350);
      }

      // Animate new value in
      const next = document.createElement('span');
      next.className = 'countdown-num--entering';
      next.textContent = newVal;
      clip.appendChild(next);

      next.addEventListener('animationend', () => {
        next.className = 'countdown-num--current';
      }, { once: true });
      // Fallback for contexts where no CSS animation is defined.
      setTimeout(() => {
        if (next && next.parentNode && next.className === 'countdown-num--entering') {
          next.className = 'countdown-num--current';
        }
      }, 350);
    }
    static addZero(x) {
      return String(x);
    }
    static resolveTargetDate({ year, month, day, hour, minute, timezone }) {
      const rawZone = (timezone || '').trim();
      const normalizedZone = CountdownTimer.normalizeTimezone(rawZone);
      const offsetMinutes = CountdownTimer.parseOffsetMinutes(normalizedZone);

      if (offsetMinutes !== null) {
        return Date.UTC(year, month - 1, day, hour, minute, 0, 0) - (offsetMinutes * 60 * 1000);
      }

      if (normalizedZone) {
        const zonedTimestamp = CountdownTimer.resolveIanaTimestamp(
          year,
          month,
          day,
          hour,
          minute,
          normalizedZone
        );

        if (!Number.isNaN(zonedTimestamp)) {
          return zonedTimestamp;
        }
      }

      return new Date(year, month - 1, day, hour, minute, 0, 0).getTime();
    }
    static normalizeTimezone(timezone) {
      if (!timezone) return '';

      const zone = timezone.replace(/^GMT/i, '').trim();
      const lower = zone.toLowerCase();

      if (lower === 'ontario' || lower === 'canada/ontario' || lower === 'toronto') {
        return 'America/Toronto';
      }

      return zone;
    }
    static parseOffsetMinutes(timezone) {
      if (!timezone) return null;

      const match = timezone.match(/^([+-])(\d{2}):?(\d{2})$/);
      if (!match) return null;

      const sign = match[1] === '-' ? -1 : 1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);

      return sign * ((hours * 60) + minutes);
    }
    static resolveIanaTimestamp(year, month, day, hour, minute, timezone) {
      const desiredUTC = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
      let guess = desiredUTC;

      for (let i = 0; i < 3; i += 1) {
        const offset = CountdownTimer.getTimezoneOffsetMs(guess, timezone);
        if (Number.isNaN(offset)) return NaN;
        const nextGuess = desiredUTC - offset;
        if (Math.abs(nextGuess - guess) < 1000) {
          guess = nextGuess;
          break;
        }
        guess = nextGuess;
      }

      return guess;
    }
    static getTimezoneOffsetMs(timestamp, timezone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        const parts = formatter.formatToParts(new Date(timestamp));
        const mapped = {};

        parts.forEach((part) => {
          if (part.type !== 'literal') {
            mapped[part.type] = parseInt(part.value, 10);
          }
        });

        const asUTC = Date.UTC(
          mapped.year,
          (mapped.month || 1) - 1,
          mapped.day || 1,
          mapped.hour || 0,
          mapped.minute || 0,
          mapped.second || 0,
          0
        );

        return asUTC - timestamp;
      } catch (error) {
        return NaN;
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);
}
